"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Compass, CreditCard, TrendingUp, MessageSquare as MessageSquareCheck, SlidersHorizontal, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const advantagesData = [{
  id: 1,
  iconName: "user-check",
  title: "Personal access to me on every project",
  description: "You speak one-on-one with the person delivering your SEO results. I personally handle your strategy, technical execution, and performance reporting, ensuring clear communication and dedicated expertise throughout.",
  badge: "1-on-1 Access"
}, {
  id: 2,
  iconName: "compass",
  title: "Tailored SEO strategies for your business",
  description: "I don't use cookie-cutter packages. Every strategy is built around your industry, competition level, target keywords, and realistic budget.",
  badge: "Custom Strategy"
}, {
  id: 3,
  iconName: "credit-card",
  title: "Transparent pricing with no hidden fees",
  description: "Clear pricing structures starting from £99 per month. I explain exactly what you're paying for with transparent deliverables and no lengthy lock-in contracts.",
  badge: "From £99/mo"
}, {
  id: 4,
  iconName: "trending-up",
  title: "Focus on measurable ROI, not vanity metrics",
  description: "I measure what matters: leads, phone calls, sales, and revenue – not just rankings for the sake of rankings.",
  badge: "Revenue First"
}, {
  id: 5,
  iconName: "message-square-check",
  title: "Honest reporting and clear updates",
  description: "You get plain-English updates, honest assessments, and monthly reports so you always understand the work completed, progress, and next steps.",
  badge: "Plain English"
}, {
  id: 6,
  iconName: "sliders-horizontal",
  title: "Flexible, adaptable approach",
  description: "If your budget changes or your priorities shift, the strategy adapts with you.",
  badge: "Agile Model"
}];
export default function Advantages() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "user-check":
        return <UserCheck className="size-5 text-primary" />;
      case "compass":
        return <Compass className="size-5 text-primary" />;
      case "credit-card":
        return <CreditCard className="size-5 text-primary" />;
      case "trending-up":
        return <TrendingUp className="size-5 text-primary" />;
      case "message-square-check":
        return <MessageSquareCheck className="size-5 text-primary" />;
      case "sliders-horizontal":
        return <SlidersHorizontal className="size-5 text-primary" />;
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  return <section id="whymyaffordableseoserviceswork" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40">
        <div className="h-96 w-96 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-medium text-secondary-foreground mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Freelance SEO Advantage</span>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl font-default">
            Why my affordable SEO services work
          </h2>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Dedicated expertise, direct communication, and practical strategies designed to deliver real search results:
          </p>
        </div>

        {/* Grid of 6 Advantage Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {advantagesData.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="flex h-full">
              <Card className="group relative flex h-full flex-col justify-between border-border bg-card/80 p-6 backdrop-blur-xs shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:border-primary/40 hover:shadow-md hover:-translate-y-1">
                <div>
                  <CardHeader className="p-0 mb-4 flex flex-row items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 transition-colors duration-200 group-hover:bg-primary/20">
                      {renderIcon(item.iconName)}
                    </div>
                    <span className="text-xs font-semibold text-primary bg-primary/5 px-2.5 py-0.5 rounded-full border border-primary/10">
                      {item.badge}
                    </span>
                  </CardHeader>

                  <CardTitle className="text-lg font-bold text-foreground mb-3 font-default">
                    {item.title}
                  </CardTitle>

                  <CardContent className="p-0">
                    <p className="text-sm leading-relaxed text-muted-foreground font-default">
                      {item.description}
                    </p>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Closing summary banner */}
        <div className="mx-auto mt-16 max-w-4xl rounded-2xl border border-border bg-card p-6 sm:p-8 text-center shadow-sm">
          <p className="text-base sm:text-lg font-medium text-foreground leading-relaxed">
            You get a streamlined, expert-led service with dedicated expertise and direct communication, focused entirely on growing your online visibility and generating real business.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/CaseStudiesPage">View My Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}